import dayjs from 'dayjs';

// Plan ready-to-use-accounting-exports.md §5 story 7, "Type -> format".
export const EXPORT_TYPES = Object.freeze({
  STATEMENT: 'statement',
  SPREADSHEET: 'spreadsheet',
  DOCUMENTS: 'documents',
});

// Spreadsheet defaults to 'xls' -- 7b's step-3 radio may change it to 'csv'. 'zip' goes through
// the same `/transactions/download` endpoint as 'pdf'/'csv'/'xls' (downloadTransactions,
// TableFilters.jsx), just with `file_format: 'zip'`.
export const EXPORT_TYPE_FORMATS = Object.freeze({
  [EXPORT_TYPES.STATEMENT]: 'pdf',
  [EXPORT_TYPES.SPREADSHEET]: 'xls',
  [EXPORT_TYPES.DOCUMENTS]: 'zip',
});

// Spreadsheet is the only 3-step flow -- step 3 (file type radio + column editor) ships in 7b.
export const getExportStepCount = (type) => (type === EXPORT_TYPES.SPREADSHEET ? 3 : 2);

// HCG-333 Exports 2.0 "Data from" tabs: Cards (the company's own card wallet, formerly the single
// implicit "Withless" source) vs Accounts (connected Open Banking accounts, unchanged).
export const EXPORT_SOURCE_TABS = Object.freeze({
  CARDS: 'cards',
  ACCOUNTS: 'accounts',
});

export const PERIOD_OPTIONS = Object.freeze({
  THIS_MONTH: 'thisMonth',
  LAST_MONTH: 'lastMonth',
  CUSTOM: 'custom',
});

// "This month" leads and is the default pick (bug report: it used to sort after "Last month" and
// "Last month" was the default, which read as an odd first impression for a wizard opened today).
// Statement hides "This month" entirely (plan §5 story 7, "Period"); its Custom is a month list
// ending at last month, not a date range -- deriveExportDateRange below is what enforces that bound.
export const getExportPeriodOptions = (type) => {
  const options = [PERIOD_OPTIONS.THIS_MONTH, PERIOD_OPTIONS.LAST_MONTH, PERIOD_OPTIONS.CUSTOM];
  return type === EXPORT_TYPES.STATEMENT
    ? options.filter((option) => option !== PERIOD_OPTIONS.THIS_MONTH)
    : options;
};

// A non-custom period resolves to a plain month, consumed via deriveExportDateRange below.
export const getExportPeriodDate = (period) => (
  period === PERIOD_OPTIONS.THIS_MONTH ? dayjs() : dayjs().subtract(1, 'month')
);

// `accountOptions` is `null` for a non-PSD2 company (TableFilters.jsx:321-322) -- one implicit
// Withless source, no selector. Otherwise filter every entry's `formats` on the tile's format,
// the one rule for all three tiles (do not hard-code a second one -- plan §5 story 7).
export const getExportSourceOptions = (accountOptions, type) => {
  if (!Array.isArray(accountOptions)) return null;
  const format = EXPORT_TYPE_FORMATS[type];
  return accountOptions.filter((option) => option.formats.includes(format));
};

// The Accounts tab is Open-Banking-only -- same per-format filter as getExportSourceOptions, minus
// the Cards-tab sentinel ("Withless"/"All cards") that still lives in the same `accountOptions`
// list. `withlessKey` is passed in rather than imported, so this stays a plain, decoupled helper
// (matches the existing test file's plain string fixtures).
export const getExportSourceTabOptions = (accountOptions, type, withlessKey) => {
  const options = getExportSourceOptions(accountOptions, type);
  return options?.filter((option) => option.value !== withlessKey) ?? options;
};

const validateDateRange = (dateRange) => Array.isArray(dateRange) && dateRange.length === 2;

// The done state's period line (review finding 1's "period is not rendered at all"). Statement's
// Custom is a single month, every other tile's Custom is a range; a non-custom period reuses the
// Select's own option label so the two stay in sync.
export const getExportPeriodLabel = ({date, dateRange, isStatement, period, periodOptions}) => {
  if (period !== PERIOD_OPTIONS.CUSTOM) return periodOptions.find((option) => option.value === period)?.label;
  if (isStatement) return date?.format('MMM YYYY');
  return validateDateRange(dateRange)
    ? `${dateRange[0].format('DD MMM YYYY')} - ${dateRange[1].format('DD MMM YYYY')}`
    : undefined;
};

// Lifted from TableFilters.jsx unchanged (plan §5 story 7). The three export handlers call
// `.format()` on both returned values, so this shape -- [dayjs, dayjs] -- must not change.
export const deriveExportDateRange = (format, date, dateRange) => {
  const isSingleDateField = format === 'pdf';
  return !isSingleDateField && validateDateRange(dateRange)
    ? dateRange
    : [date.startOf('month'), date.endOf('month')];
};
